/**
 * Minimal I/O surface PatternPlayer needs — engine adapter or Worker sink.
 */
import type { InstrumentSlot, VoiceNoteFxParams } from "../types";
import type { AudioEngine, AudioEngineMode } from "./engine";

export type SchedulerHost = {
  readonly mode: AudioEngineMode;
  getCurrentTime(): number;
  advanceTime?(seconds: number): void;
  setBpm(bpm: number): void;
  play(): void;
  stop(): void;
  allNotesOff(): void;
  /** Drop kernel pending note traffic with time > afterTime. */
  cancelPending(afterTime: number): void;
  noteOn(
    slot: InstrumentSlot,
    note: number,
    velocity?: number,
    time?: number,
    noteFx?: VoiceNoteFxParams,
  ): void;
  noteOff(slot: InstrumentSlot, note?: number, time?: number): void;
  setNoteFx(
    slot: InstrumentSlot,
    noteFx: VoiceNoteFxParams,
    time?: number,
    velocity?: number,
  ): void;
};

/** Adapt a live AudioEngine into the scheduler host interface. */
export function createEngineSchedulerHost(engine: AudioEngine): SchedulerHost {
  return {
    get mode() {
      return engine.mode;
    },
    getCurrentTime: () => engine.getCurrentTime(),
    advanceTime: seconds => {
      engine.advanceTime(seconds);
    },
    setBpm: bpm => {
      engine.transport.setBpm(bpm);
    },
    play: () => {
      engine.transport.play();
    },
    stop: () => {
      engine.transport.stop();
    },
    allNotesOff: () => {
      engine.transport.allNotesOff();
    },
    cancelPending: afterTime => {
      engine.transport.cancelPending(afterTime);
    },
    noteOn: (slot, note, velocity, time, noteFx) => {
      engine.transport.noteOn(slot, note, velocity, time, noteFx);
    },
    noteOff: (slot, note, time) => {
      engine.transport.noteOff(slot, note, time);
    },
    setNoteFx: (slot, noteFx, time, velocity) => {
      engine.transport.setNoteFx(slot, noteFx, time, velocity);
    },
  };
}

export function isAudioEngine(value: AudioEngine | SchedulerHost): value is AudioEngine {
  return (
    typeof value === "object"
    && value !== null
    && "transport" in value
    && "getTelemetry" in value
  );
}
